/**
 * How long one attempt to load a piece of artwork may run before the loader
 * gives up on it and retries.
 *
 * The deadline exists for a request that will never answer: no `load`, no
 * `error`, a connection that silently died. Nothing else needs it, because
 * every ordinary failure (DNS, refused, reset, a 4xx/5xx, a network change)
 * fires `error` on its own. And the deadline is DESTRUCTIVE: the retry drops
 * `src`, which aborts the download and throws away every byte received, and
 * the next attempt starts from zero. So it must be longer than any healthy
 * download, or a slow link never finishes anything.
 *
 * It was 3 seconds. Measured with 13 real card arts (209–586 KB each, the
 * Collection's first page) through a CDP throttle: 50 and 20 Mbps loaded all
 * 13 in one round; 10 Mbps needed 20 requests for 13 images; 5 Mbps needed 33
 * requests and 21 s where 7.5 s would have done; 2 Mbps loaded 5 of 13 in 60 s
 * across 61 requests; 1 Mbps loaded none in 65 requests. Below ~5 Mbps the
 * loader was cancelling downloads that were about to finish and restarting
 * them, forever. A single art that alone needs more than 3 s (5 s of latency
 * in the slow-art probe) NEVER loaded: 5 requests, 0 pixels in 45 s.
 *
 * The ladder: 30 s for the first attempt, doubling on each retry to 120 s.
 * 30 s covers that whole first page down to roughly 2 Mbps; the doubling is
 * for the one image that outlasted its window anyway (a 1.5 MB portrait on a
 * 300 kbps line takes 40 s), which now gets a long enough attempt instead of
 * the same too-short one again. A genuinely dead request still shows the
 * retrying well — 30 s later than before, on a failure `error` never fires
 * for, which is the rare one.
 *
 * Shared by `cards.js` (`loadCardArtImage`) and `retrying-image.ts`; the wait
 * BETWEEN attempts is their own 1/3/8/20/30-second ladder, unchanged. The
 * summoner select's portrait fetches (`deck-selector.js`) take the same
 * windows since 28 September 2026: at 12 s each, on 1 Mbps beside the
 * Collection's page, its 521 KB and 835 KB portraits were aborted twice and
 * never arrived.
 */
const IMAGE_ATTEMPT_DEADLINES_MS = Object.freeze([30000, 60000, 120000]);

/**
 * @param {number} [attempt] how many attempts have already failed for this image
 * @returns {number} milliseconds the next attempt may take before it is retried
 */
function imageAttemptDeadlineMs(attempt = 0) {
  const failed = Number.isFinite(Number(attempt)) ? Math.max(0, Math.floor(Number(attempt))) : 0;
  return IMAGE_ATTEMPT_DEADLINES_MS[Math.min(failed, IMAGE_ATTEMPT_DEADLINES_MS.length - 1)];
}

export { IMAGE_ATTEMPT_DEADLINES_MS, imageAttemptDeadlineMs };
